import { Activity, GroupedActivity, FileMatching } from './interfaces';

export const groupActivitiesByProjectAndGroup = (activities: Activity[]): GroupedActivity[] => {
  const projectMap = new Map<string, Map<string, Activity[]>>();

  // Agrupar por proyecto y luego por grupo
  activities.forEach((activity) => {
    const projectName = activity.projectName || 'Sin Proyecto';
    const groupKey = `${activity.groupAssigned}_${activity.projectName}`;

    if (!projectMap.has(projectName)) {
      projectMap.set(projectName, new Map());
    }

    const groupMap = projectMap.get(projectName)!;
    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }

    groupMap.get(groupKey)!.push(activity);
  });

  // Convertir a estructura GroupedActivity
  const result: GroupedActivity[] = [];

  projectMap.forEach((groupMap, projectName) => {
    const groups: GroupedActivity['groups'] = [];

    groupMap.forEach((groupActivities, groupKey) => {
      const firstActivity = groupActivities[0];
      const engineers = Array.from(new Set(groupActivities.map(a => a.executedUser))).join(', ');
      const totalActivities = groupActivities.length;
      const shouldHaveCards = groupActivities.filter(a => a.shouldHasCardsLoaded).length;
      const hasCards = groupActivities.filter(a => a.hasCardsLoaded).length;

      groups.push({
        groupAssigned: firstActivity.groupAssigned,
        activities: groupActivities,
        engineers,
        totalActivities,
        shouldHaveCards,
        hasCards,
      });
    });

    if (groups.length > 0) {
      result.push({
        projectName,
        projectId: groups[0].activities[0].projectId,
        groups,
      });
    }
  });

  return result;
};

export const cleanFileName = (fileName: string, projectName: string): string => {
  // Eliminar extensión
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  
  // Intentar extraer del nuevo formato: LS 0577_DYN_... o LB 0577_DYN_...
  // Patrón: [LS|LB] seguido de espacio opcional, luego número de 4 dígitos, luego guion bajo
  const newFormatMatch = nameWithoutExt.match(/^(LS|LB)\s*(\d{4})_/i);
  if (newFormatMatch) {
    const prefix = newFormatMatch[1].toUpperCase();
    const fourDigits = newFormatMatch[2];
    return `${prefix} ${fourDigits}`;
  }

  // Intentar extraer del formato anterior: LS 0577 o LB 0577 (con o sin espacio)
  const oldFormatMatch = nameWithoutExt.match(/^(LS|LB)\s*(\d{4})/i);
  if (oldFormatMatch) {
    const prefix = oldFormatMatch[1].toUpperCase();
    const fourDigits = oldFormatMatch[2];
    return `${prefix} ${fourDigits}`;
  }
  
  // Extraer solo números (cualquier secuencia de dígitos) - lógica anterior como fallback
  const numbers = nameWithoutExt.match(/\d+/g);
  
  if (!numbers || numbers.length === 0) {
    return '';
  }

  // Tomar la última secuencia de 4 dígitos o la más larga si no hay de 4
  let fourDigits = '';
  for (let i = numbers.length - 1; i >= 0; i--) {
    if (numbers[i].length >= 4) {
      fourDigits = numbers[i].slice(-4); // Tomar últimos 4 dígitos
      break;
    }
  }

  // Si no hay secuencia de 4 dígitos, usar la más larga
  if (!fourDigits && numbers.length > 0) {
    const longest = numbers.reduce((a, b) => a.length > b.length ? a : b);
    fourDigits = longest.slice(-4).padStart(4, '0');
  }

  if (!fourDigits) {
    return '';
  }

  // Determinar prefijo según el proyecto
  let prefix = '';
  if (projectName.toLowerCase().includes('lagunillas')) {
    prefix = 'LS';
  } else if (projectName.toLowerCase().includes('bachaquero')) {
    prefix = 'LB';
  } else {
    // Intentar extraer prefijo del nombre original si existe
    const prefixMatch = nameWithoutExt.match(/^([A-Z]{2})\s*\d+/i);
    if (prefixMatch) {
      prefix = prefixMatch[1].toUpperCase();
    } else {
      prefix = 'LS'; // Default
    }
  }

  return `${prefix} ${fourDigits}`;
};

/**
 * Extrae el número de 4 dígitos del nombre del archivo para comparar con locationId
 * @param fileName Nombre del archivo
 * @returns Número de 4 dígitos como string, o null si no se encuentra
 */
export const extractLocationNumber = (fileName: string): string | null => {
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  
  // Intentar extraer del nuevo formato: LS 0577_DYN_... o LB 0577_DYN_...
  const newFormatMatch = nameWithoutExt.match(/^(LS|LB)\s*(\d{4})_/i);
  if (newFormatMatch) {
    return newFormatMatch[2];
  }

  // Intentar extraer del formato anterior: LS 0577 o LB 0577
  const oldFormatMatch = nameWithoutExt.match(/^(LS|LB)\s*(\d{4})/i);
  if (oldFormatMatch) {
    return oldFormatMatch[2];
  }

  // Fallback: buscar cualquier secuencia de 4 dígitos
  const numbers = nameWithoutExt.match(/\d+/g);
  if (numbers && numbers.length > 0) {
    for (let i = numbers.length - 1; i >= 0; i--) {
      if (numbers[i].length >= 4) {
        return numbers[i].slice(-4);
      }
    }
    // Si no hay de 4 dígitos, usar la más larga
    const longest = numbers.reduce((a, b) => a.length > b.length ? a : b);
    return longest.slice(-4).padStart(4, '0');
  }

  return null;
};

export const matchFilesToActivities = (
  files: File[],
  activities: Activity[],
  projectName: string
): FileMatching[] => {
  const matchings: FileMatching[] = [];
  const usedFiles = new Set<File>();

  // Primero, intentar hacer match automático
  activities.forEach((activity) => {
    if (!activity.shouldHasCardsLoaded) {
      matchings.push({
        activityId: activity.id,
        activity,
        matchedFiles: [],
        status: 'pending',
      });
      return;
    }

    const matchedFilesForActivity: File[] = [];
    
    // Buscar todos los archivos que coincidan con esta actividad
    for (const file of files) {
      if (usedFiles.has(file)) continue;

      // Intentar match por locationId primero (nuevo formato)
      const locationNumber = extractLocationNumber(file.name);
      if (locationNumber && activity.locationId === locationNumber) {
        matchedFilesForActivity.push(file);
        usedFiles.add(file);
        continue;
      }

      // Fallback: intentar match por locationName (formato anterior)
      const cleanedName = cleanFileName(file.name, projectName);
      if (cleanedName === activity.locationName) {
        matchedFilesForActivity.push(file);
        usedFiles.add(file);
      }
    }

    if (matchedFilesForActivity.length > 0) {
      matchings.push({
        activityId: activity.id,
        activity,
        matchedFiles: matchedFilesForActivity,
        status: 'matched',
      });
    } else {
      matchings.push({
        activityId: activity.id,
        activity,
        matchedFiles: [],
        status: 'missing',
      });
    }
  });

  // Identificar archivos que no coincidieron con ninguna actividad que requiere carta
  files.forEach((file) => {
    if (!usedFiles.has(file)) {
      const locationNumber = extractLocationNumber(file.name);
      const cleanedName = cleanFileName(file.name, projectName);
      
      // Buscar si hay alguna actividad que no requiere carta
      const activityWithoutCard = activities.find(
        (a) => !a.shouldHasCardsLoaded && (
          (locationNumber && a.locationId === locationNumber) ||
          cleanedName === a.locationName
        )
      );

      if (activityWithoutCard) {
        // Buscar si ya existe un matching para esta actividad
        let existingMatching = matchings.find(m => m.activityId === activityWithoutCard.id);
        if (!existingMatching) {
          existingMatching = {
            activityId: activityWithoutCard.id,
            activity: activityWithoutCard,
            matchedFiles: [],
            status: 'error',
          };
          matchings.push(existingMatching);
        }
        existingMatching.matchedFiles.push(file);
        existingMatching.status = 'error';
        usedFiles.add(file);
      }
    }
  });

  return matchings;
};

export const getUnmatchedFiles = (
  files: File[],
  matchings: FileMatching[]
): File[] => {
  const matchedFiles = new Set<File>();
  
  matchings.forEach(matching => {
    matching.matchedFiles.forEach(file => {
      matchedFiles.add(file);
    });
  });

  return files.filter(file => !matchedFiles.has(file));
};

/**
 * Interfaz para los datos parseados de un archivo de carta dinámica
 */
export interface ParsedDynCardData {
  'Surface Card Position': number[];
  'Surface Card Load': number[];
  'Pump Card Position': number[];
  'Pump Card Load': number[];
}

/**
 * Parsea un archivo .txt de carta dinámica y extrae los datos numéricos
 * @param file Archivo .txt a parsear
 * @returns Objeto con arrays de números para cada columna, o null si hay error
 */
export const parseDynCardTxtFile = async (
  file: File
): Promise<ParsedDynCardData | null> => {
  try {
    // Leer el archivo como texto
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });

    const lines = text.split('\n').filter(line => line.trim().length > 0);
    
    if (lines.length < 2) {
      throw new Error('El archivo debe tener al menos una línea de encabezado y una línea de datos');
    }

    // Extraer headers de la primera línea (removiendo # y dividiendo por tabs)
    const headerLine = lines[0].replace(/^#/, '').trim();
    const headers = headerLine.split('\t').map(h => h.trim()).filter(h => h.length > 0);

    // Validar que las columnas coincidan con las esperadas
    const expectedHeaders = [
      'Surface Card Position',
      'Surface Card Load',
      'Pump Card Position',
      'Pump Card Load'
    ];

    const headerMap: { [key: string]: number } = {};
    expectedHeaders.forEach(expectedHeader => {
      const index = headers.findIndex(h => h.toLowerCase() === expectedHeader.toLowerCase());
      if (index === -1) {
        throw new Error(`Columna requerida no encontrada: ${expectedHeader}`);
      }
      headerMap[expectedHeader] = index;
    });

    // Inicializar arrays de datos
    const data: ParsedDynCardData = {
      'Surface Card Position': [],
      'Surface Card Load': [],
      'Pump Card Position': [],
      'Pump Card Load': []
    };

    // Parsear filas de datos (valores numéricos separados por tabs)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Saltar líneas vacías

      const values = line.split('\t').map(v => v.trim());
      
      // Parsear cada columna esperada
      expectedHeaders.forEach(header => {
        const index = headerMap[header];
        if (index < values.length) {
          const value = parseFloat(values[index]);
          if (!isNaN(value)) {
            data[header as keyof ParsedDynCardData].push(value);
          }
        }
      });
    }

    // Validar que todos los arrays tengan la misma longitud
    const lengths = expectedHeaders.map(h => data[h as keyof ParsedDynCardData].length);
    const uniqueLengths = new Set(lengths);
    
    if (uniqueLengths.size > 1) {
      throw new Error('Los arrays de datos no tienen la misma longitud');
    }

    if (lengths[0] === 0) {
      throw new Error('No se encontraron datos válidos en el archivo');
    }

    return data;
  } catch (error: any) {
    console.error(`Error parseando archivo ${file.name}:`, error);
    throw new Error(`Error parseando ${file.name}: ${error.message}`);
  }
};

/**
 * Interfaz para los datos formateados para el endpoint
 */
export interface FormattedCardUploadData {
  activityId: string;
  value: Array<{
    location: string;
    'Surface Card Position': number[];
    'Surface Card Load': number[];
    'Pump Card Position': number[];
    'Pump Card Load': number[];
    type: string;
  }>;
}

/**
 * Formatea los datos de cartas para subir al endpoint
 * @param fileMatching Array de matchings de archivos con actividades
 * @param projectName Nombre del proyecto (para determinar prefijo LS/LB)
 * @returns Array de objetos formateados para el endpoint
 */
export const formatCardDataForUpload = async (
  fileMatching: FileMatching[],
  projectName: string
): Promise<FormattedCardUploadData[]> => {
  const uploadDataMap = new Map<string, FormattedCardUploadData>();

  // Filtrar solo las actividades con archivos asociados y estado matched
  const matchedActivities = fileMatching.filter(
    m => m.matchedFiles.length > 0 && m.status === 'matched'
  );

  for (const matching of matchedActivities) {
    const { activityId, matchedFiles } = matching;

    // Si no existe entrada para esta actividad, crearla
    if (!uploadDataMap.has(activityId)) {
      uploadDataMap.set(activityId, {
        activityId,
        value: []
      });
    }

    const uploadData = uploadDataMap.get(activityId)!;

    // Parsear cada archivo asociado a esta actividad
    for (const file of matchedFiles) {
      try {
        // Parsear el archivo .txt
        const parsedData = await parseDynCardTxtFile(file);
        
        if (!parsedData) {
          continue; // Si no se pudo parsear, continuar con el siguiente
        }

        // Extraer location del nombre del archivo usando cleanFileName para obtener formato "LS 0577"
        const location = cleanFileName(file.name, projectName);
        
        if (!location) {
          throw new Error(`No se pudo extraer location del nombre del archivo: ${file.name}`);
        }

        // Formatear como objeto card data según especificación del endpoint
        uploadData.value.push({
          location,
          'Surface Card Position': parsedData['Surface Card Position'],
          'Surface Card Load': parsedData['Surface Card Load'],
          'Pump Card Position': parsedData['Pump Card Position'],
          'Pump Card Load': parsedData['Pump Card Load'],
          type: 'echometer'
        });
      } catch (error: any) {
        console.error(`Error procesando archivo ${file.name} para actividad ${activityId}:`, error);
        // Continuar con el siguiente archivo aunque este falle
      }
    }
  }

  // Retornar solo las actividades que tienen al menos un archivo parseado correctamente
  return Array.from(uploadDataMap.values()).filter(item => item.value.length > 0);
};

